/**
 * Announcement Banner Component
 *
 * Displays a single announcement banner that appears under the header.
 * Shows the announcement title and type, and allows clicking to view full message.
 * Dismissible announcements show an X button.
 */

import { memo, useState, useEffect } from "react";
import { Info, AlertTriangle, X } from "lucide-react";
import { Modal } from "@/ui/atoms";
import { cn } from "@/utils/utils";

export interface TAnnouncementBannerProps {
    announcement: {
        id: string;
        title: string;
        message: string;
        type: "info" | "warning";
        is_active: boolean;
    } | null;
    onDismiss: (announcementId: string) => Promise<void>;
    isLoading?: boolean;
}

export const AnnouncementBanner = memo(function AnnouncementBanner({
    announcement,
    onDismiss,
    isLoading = false,
}: TAnnouncementBannerProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDismissing, setIsDismissing] = useState(false);

    // Reset modal state when announcement changes
    useEffect(() => {
        setIsModalOpen(false);
    }, [announcement?.id]);

    if (isLoading || !announcement) {
        return null;
    }

    const isInfo = announcement.type === "info";
    const Icon = isInfo ? Info : AlertTriangle;

    const handleBannerClick = () => {
        setIsModalOpen(true);
    };

    const handleDismiss = async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent opening modal when clicking X
        if (isDismissing) return;

        setIsDismissing(true);
        try {
            await onDismiss(announcement.id);
        } catch (error) {
            console.error("Failed to dismiss announcement:", error);
        } finally {
            setIsDismissing(false);
        }
    };

    return (
        <>
            <div
                className={cn(
                    "w-full border-b transition-colors cursor-pointer",
                    isInfo
                        ? "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                        : "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100",
                )}
                onClick={handleBannerClick}
            >
                <div className="container mx-auto px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Icon
                                className={cn(
                                    "h-5 w-5 flex-shrink-0",
                                    isInfo ? "text-blue-600" : "text-amber-600",
                                )}
                            />
                            <span className="font-medium truncate">{announcement.title}</span>
                        </div>
                        {!announcement.is_active && (
                            <button
                                onClick={handleDismiss}
                                disabled={isDismissing}
                                className={cn(
                                    "p-1 rounded hover:bg-black/10 transition-colors flex-shrink-0",
                                    isInfo ? "text-blue-700" : "text-amber-700",
                                    isDismissing && "opacity-50 cursor-not-allowed",
                                )}
                                title="Dismiss"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <Modal open={isModalOpen} onOpenChange={setIsModalOpen} title={announcement.title}>
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Icon
                            className={cn("h-5 w-5", isInfo ? "text-blue-600" : "text-amber-600")}
                        />
                        <span className="text-sm font-medium capitalize">{announcement.type}</span>
                    </div>
                    <div className="prose prose-sm max-w-none">
                        <p className="whitespace-pre-wrap">{announcement.message}</p>
                    </div>
                </div>
            </Modal>
        </>
    );
});
